import React, { useRef, useEffect, useState } from 'react';
import cx from 'classnames';

import { FormattedMessage, useIntl } from 'react-intl';

import {
  LazyLoading,
  useEditContext,
  usePageContext,
  EmptyState,
} from '@xingternal/pages-common';

import type {
  LAZY_LOADING_TYPE_BUTTON,
  LAZY_LOADING_TYPE_SCROLL,
} from '@xingternal/pages-common';

import { Button } from '@xingternal/button';
import { SocialCountersProvider } from '@xingternal/social-bar';

import {
  feedMetadataHasElements,
  getBannerTextResource,
  getEmptyStateTextResources,
  getEmptyTopAreaTextResources,
  getFatalErrorTextResources,
  getPinnedArticle,
  getSubPageKey,
  isValidFeedItem,
  newsPath,
  searchForFatalErrorInModule,
} from '../utils';
import { PUBLISHED, PAID_PLUS, PAID } from '../constants';
import { useEntityPageFeedQuery, EntityPageFeedQueryVariables } from '../FeedQuery.gql-types';
import { EntityPageFeedCollectionFragment } from '../EntityPageFeedCollectionFragment.gql-types';

import styles from './News.css';
import ModuleError from './ModuleError';
import { NewsItem, LoadingSkeleton } from '../NewsItem';
import NewsNavigation from './NewsNavigation';
import { CommboxOmTrigger, CommboxOnloadOmTrigger } from '../NewsCommbox';
import NewsPremiumBanner from './NewsPremiumBanner';


export type NewsProps = {
  actor: {
    title: string,
    slug: string,
    logo?: string,
    isInsider: boolean,
  },
  focusType: string,
  globalId: string | null,
  isDetailPage?: boolean,
  isFollowingPage?: boolean,
  lazyLoadingIsAllowed: boolean,
  lazyLoadingType: typeof LAZY_LOADING_TYPE_BUTTON | typeof LAZY_LOADING_TYPE_SCROLL | null,
  loadingSkeletonCount?: number,
  onClick?: () => void,
  setLazyLoadingIsAllowed: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedPosting?: React.Dispatch<React.SetStateAction<EntityPageFeedCollectionFragment["object"] | null>>;
  setHasErrorOnSubpageLabel: React.Dispatch<React.SetStateAction<boolean>>,
  showActors?: boolean,
  showSubpageLinks?: boolean,
  setCurrentPublicationState: (currentPublicationState: any) => void,
  feedQueryVars: EntityPageFeedQueryVariables,

}
const buildSkeletonLoader = (loadingSkeletonCount: number, showActors: boolean) => (
  <div data-testid={'NEWS_MODULE_SKELETON'}>
    {[...Array(loadingSkeletonCount)].map((_, i) => (
      <LoadingSkeleton key={i} smallActor={!showActors} />
    ))}
  </div>
);

// eslint-disable-next-line complexity
const News = ({
  actor,
  focusType,
  isDetailPage = false,
  isFollowingPage = false,
  lazyLoadingIsAllowed,
  lazyLoadingType,
  loadingSkeletonCount = 1,
  setHasErrorOnSubpageLabel,
  setLazyLoadingIsAllowed,
  setSelectedPosting,
  showActors = true,
  showSubpageLinks = true,
  setCurrentPublicationState,
  feedQueryVars,
  onClick,
}: NewsProps) => {
  const intl = useIntl();
  const videoPlaying = useRef();
  const [isRefetching, setIsRefetching] = useState(false);
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();
  const {
    data,
    loading,
    error,
    refetch,
    fetchMore,
  } = useEntityPageFeedQuery({
    variables: feedQueryVars,
    notifyOnNetworkStatusChange: false, // true is creating a 2nd request on refetch without the after variable
    errorPolicy: 'all',
  });
  const { contractType } = pageContext;
  const feedItems = data?.entityPageContentFeeds?.collection || [];
  const hasFatalError = error && searchForFatalErrorInModule(error.graphQLErrors, data);
  const isPaidPage = contractType === PAID_PLUS || contractType === PAID;
  const linkTextResource = getBannerTextResource(intl);
  const metadata = data?.entityPageContentFeeds?.metadata;
  const metadataHasElements = feedMetadataHasElements(metadata);
  const subPageKey = getSubPageKey(isEditing);
  const topNewsItem = isPaidPage ? getPinnedArticle(feedItems) : null;
  const showNewsNavigation =
    isDetailPage &&
    isEditing &&
    (metadataHasElements || feedQueryVars.filter !== PUBLISHED);
  const showTopItem =
    isPaidPage && topNewsItem && feedQueryVars.filter === PUBLISHED;
  // const showLazyLoading =
  //   !hasFatalError && lazyLoadingType && feedItems.length > 0;
  const showTopNewsEmptyState =
    isPaidPage &&
    !topNewsItem &&
    isEditing &&
    feedQueryVars.filter === PUBLISHED;
  const showFatalErrorEmptyState =
    feedItems.length === 0 &&
    !loading &&
    !isEditing &&
    data &&
    data.entityPageContentFeeds;
  const loadingFirstRender = loading && !isRefetching;

  const lazyLoad =
    // Check if we have more than one element so this function is not triggered
    // when the regular query is being done
    feedItems.length > 0 && fetchMore
      ? () => {
          const object = feedItems[feedItems.length - 1].object;
          const limit = feedQueryVars.limit;
          const until =
            feedQueryVars.filter === PUBLISHED
              ? feedItems[feedItems.length - 1].publishedAt
              : object?.__typename === "EntityPageContentFeedPostingPost" ? object?.createdAt : undefined;

          setIsRefetching(true);
          fetchMore({
            variables: { limit, until },
            updateQuery: (prev: any, { fetchMoreResult }) => {
              setIsRefetching(false);
              if (!fetchMoreResult) {
                setLazyLoadingIsAllowed(false);
                return prev;
              } else {
                const newItems =
                  fetchMoreResult?.entityPageContentFeeds?.collection || [];
                setLazyLoadingIsAllowed(newItems.length > 0);
                return {
                  entityPageContentFeeds: {
                    ...prev.entityPageContentFeeds,
                    collection: [
                      ...prev.entityPageContentFeeds.collection,
                      ...newItems,
                    ],
                  },
                };
              }
            },
          });
        }
      : () => {};

  // gather all activity urns for providing social counter data below
  const allUrns = feedItems.map((f) => f.globalId);
  const wrapperProps =
    feedQueryVars.filter === PUBLISHED && !loading ? { urns: allUrns, children: React.Fragment } : undefined;
  const Wrapper =
     wrapperProps
      ? SocialCountersProvider
      : React.Fragment;

  const showEmptyState =
    !hasFatalError &&
    feedItems.length === 0 &&
    !loading &&
    isEditing &&
    data &&
    !!data.entityPageContentFeeds;

  const emptyStateCopy = getEmptyStateTextResources(
    showEmptyState,
    isDetailPage,
    metadataHasElements
  );

  const fatalErrorCopy = getFatalErrorTextResources();
  const topAreaCopy = getEmptyTopAreaTextResources();

  useEffect(() => {
    typeof hasFatalError !== "undefined" && setHasErrorOnSubpageLabel(hasFatalError);
  }, [data, error]);

  return (
    <Wrapper {...wrapperProps!}>
      <div
        data-testid={'NEWS_MODULE_CONTAINER'}
        className={cx(styles.content, {
          [styles.noHeader]: !showActors,
        })}
      >
        {showEmptyState && (
          <div data-testid={'NEWS_MODULE'}>
            <EmptyState
              {...emptyStateCopy}
              className={styles.emptyTopAreaWrapper}
            />
          </div>
        )}

        {onClick && <CommboxOmTrigger onClick={onClick} />}
        {onClick && !isDetailPage && (
          <CommboxOnloadOmTrigger onClick={onClick} />
        )}
        {showNewsNavigation && metadata && (
          <NewsNavigation
            isLoading={loading}
            metadata={metadata}
            pageId={feedQueryVars.id}
            feedQueryVars={feedQueryVars}
            publicationState={feedQueryVars.filter}
            setPublicationState={setCurrentPublicationState}
          />
        )}
        <>
          {loadingFirstRender &&
            buildSkeletonLoader(loadingSkeletonCount, showActors)}

          {!loadingFirstRender && hasFatalError && (
            <div data-testid={'NEWS_MODULE'}>
              <ModuleError refetch={refetch} />
            </div>
          )}

          {!hasFatalError && (
            <>
              {showFatalErrorEmptyState && (
                <div data-testid={'NEWS_MODULE'}>
                  <EmptyState
                    {...fatalErrorCopy}
                    className={styles.emptyTopAreaWrapper}
                  />
                </div>
              )}

              {feedItems.length > 0 && data && data.entityPageContentFeeds && (
                <div data-testid={'NEWS_MODULE'}>
                  {!isPaidPage && isEditing && (
                    <NewsPremiumBanner
                      buttonTextKey="TOP_ARTICLE_PAID_CTA"
                      flagTextKey="TOP_ARTICLE_PAID_FLAG"
                      focusType={focusType}
                      headlineCopyKey="TOP_ARTICLE_PAID_HEADLINE"
                      href={linkTextResource}
                      locale={intl.locale}
                      moduleName="newsmodule"
                      testId={'newsPremiumBanner'}
                      textCopyKey="TOP_ARTICLE_PAID_COPY"
                    />
                  )}
                  {showTopNewsEmptyState && (
                    <div className={styles.emptyTopAreaWrapper}>
                      <EmptyState
                        {...topAreaCopy}
                        className={styles.emptyTopAreaWrapper}
                        testId={'topEmptyArea'}
                      />
                    </div>
                  )}
                  {showTopItem && topNewsItem && (
                    <NewsItem
                      actor={actor}
                      feedQueryVars={feedQueryVars}
                      isDetailPage={isDetailPage}
                      isFollowingPage={isFollowingPage}
                      isPaidPage={isPaidPage}
                      feedUpdate={topNewsItem}
                      onClick={onClick}
                      setLazyLoadingIsAllowed={setLazyLoadingIsAllowed}
                      showActor={showActors}
                      videoPlaying={videoPlaying}
                      selectedPosting={() => {
                        setSelectedPosting && setSelectedPosting(topNewsItem.object);
                      }}
                    />
                  )}
                </div>
              )}

              {feedItems.map((feedItem) => {
                if (isValidFeedItem(feedItem, feedQueryVars) && feedItem?.object?.__typename !== "EntityPageContentFeedPlainTextPostingPost") {
                  return (
                    <NewsItem
                      key={feedItem?.object?.globalId}
                      actor={actor}
                      feedQueryVars={feedQueryVars}
                      isDetailPage={isDetailPage}
                      isFollowingPage={isFollowingPage}
                      feedUpdate={feedItem}
                      isPaidPage={isPaidPage}
                      onClick={onClick}
                      setLazyLoadingIsAllowed={setLazyLoadingIsAllowed}
                      showActor={showActors}
                      videoPlaying={videoPlaying}
                      selectedPosting={() => {
                        setSelectedPosting && setSelectedPosting(feedItem.object);
                      }}
                    />
                  );
                }
                return null;
              })}
            </>
          )}

          <LazyLoading
            type={lazyLoadingType}
            isLoading={isRefetching}
            enabled={lazyLoadingIsAllowed}
            onLazyLoad={() => lazyLoad()}
            dotAnimation={true}
          />

          {showSubpageLinks && feedItems.length > 0 && (
            <div className={styles.lazyloadContainer}>
              <Button
                variant={'secondary'}
                size={'small'}
                to={newsPath(pageContext?.basePath, actor.slug)}
              >
                <FormattedMessage id={subPageKey} />
              </Button>
            </div>
          )}
        </>
      </div>
    </Wrapper>
  );
};

export default News;
